import { clsx } from "clsx";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useTableContext } from "./context";
import type { ITableColumn } from "./types";

type Props = {
  column: ITableColumn;
  className?: string;
};

export function TableHeaderCell({ column, className }: Props) {
  const { sorting, onSort } = useTableContext();

  const isSorted = sorting?.key === column.key;
  const sortDirection = isSorted ? sorting.direction : null;

  const handleSort = () => {
    if (column.sortable && onSort) {
      onSort(column.sortKey || column.key);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && column.sortable && onSort) {
      e.preventDefault();
      onSort(column.key);
    }
  };

  const getAriaSort = () => {
    if (!column.sortable) return undefined;
    if (!isSorted) return "none";
    return sortDirection === "asc" ? "ascending" : "descending";
  };

  const getAriaLabel = () => {
    if (!column.sortable) return undefined;
    if (!isSorted) return `Sort by ${column.name}`;
    if (sortDirection === "asc") {
      return `Sorted by ${column.name} in ascending order`;
    }
    return `Sorted by ${column.name} in descending order`;
  };

  return (
    <div
      className={clsx(
        "nuco-table__header-cell",
        `nuco-table__header-cell--align-${column.align}`,
        column.sortable && "nuco-table__header-cell--sortable",
        className,
      )}
      onClick={handleSort}
      onKeyDown={handleKeyDown}
      role="columnheader"
      tabIndex={column.sortable ? 0 : undefined}
      aria-sort={getAriaSort()}
      aria-label={getAriaLabel()}
    >
      <div className="nuco-table__header-cell-inner">
        <span>{column.name}</span>
        {column.sortable && (
          <div className="nuco-table__header-cell-inner-iconBox">
            {sortDirection === "asc" && (
              <ArrowUpIcon
                size="0.875rem"
                className="nuco-table__header-cell-inner-icon"
              />
            )}
            {sortDirection === "desc" && (
              <ArrowDownIcon
                size="0.875rem"
                className="nuco-table__header-cell-inner-icon"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
